import { useEffect, useState } from 'react';
import { Box, Modal, Stack, TextField } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import { colors } from 'styles/colors';
import { RawProductDescriptionPatch } from 'services/rawProductService';

interface Props {
  // Null when no card is being edited. The whole description is one edit rather
  // than a field at a time: the two sentences are shown side by side and read
  // against each other, so correcting one usually means looking at the other.
  rawProductId: string | null;
  productName: string;
  descriptionEn: string | null;
  descriptionAr: string | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (patch: RawProductDescriptionPatch) => void;
}

export default function DescriptionModal({
  rawProductId,
  productName,
  descriptionEn,
  descriptionAr,
  isSaving,
  onClose,
  onSave,
}: Props) {
  const [en, setEn] = useState('');
  const [ar, setAr] = useState('');

  // Opened on what the row holds every time rather than on whatever was left
  // here last: an abandoned edit that came back on the next open would read as a
  // stored sentence.
  useEffect(() => {
    if (!rawProductId) return;
    setEn(descriptionEn ?? '');
    setAr(descriptionAr ?? '');
  }, [rawProductId, descriptionEn, descriptionAr]);

  const changed = en.trim() !== (descriptionEn ?? '').trim() || ar.trim() !== (descriptionAr ?? '').trim();

  return (
    <Modal open={Boolean(rawProductId)} onClose={isSaving ? undefined : onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 3,
          width: 720,
          maxWidth: '92vw',
        }}
      >
        <CustomText text="Edit description" fontWeight={700} fontSize={18} />
        <Box mt={0.5}>
          <CustomText text={productName} fontSize={13} color={colors.gray} />
        </Box>

        {/* Side by side in the same arrangement as the card, because the Arabic
            is not a translation of the English but a second reading of the same
            pack: where they disagree about what the product is, the disagreement
            is the thing worth seeing while correcting either. */}
        <Box
          mt={2}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: 2,
            rowGap: 2,
          }}
        >
          <Stack spacing={0.75}>
            <CustomText text="EN" fontSize={12} fontWeight={600} color={colors.gray} />
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={6}
              value={en}
              disabled={isSaving}
              onChange={(event) => setEn(event.target.value)}
              placeholder="—"
              autoFocus
            />
          </Stack>
          <Stack spacing={0.75}>
            <CustomText text="AR" fontSize={12} fontWeight={600} color={colors.gray} />
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={6}
              value={ar}
              disabled={isSaving}
              onChange={(event) => setAr(event.target.value)}
              placeholder="—"
              inputProps={{ dir: 'rtl' }}
            />
          </Stack>
        </Box>

        <Box mt={1}>
          <CustomText
            text="What the pack is and what it is for, in a sentence. An empty box says this product has no description, which is what promotion waits on."
            fontSize={11}
            color={colors.gray}
          />
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <CustomButton
            text="Cancel"
            variant="outlined"
            height={40}
            disabled={isSaving}
            onClick={onClose}
          />
          <CustomButton
            text="Save"
            height={40}
            isLoading={isSaving}
            disabled={!changed}
            onClick={() =>
              onSave({ descriptionEn: en.trim() || null, descriptionAr: ar.trim() || null })
            }
          />
        </Stack>
      </Box>
    </Modal>
  );
}
